import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, X, Database, ExternalLink, Maximize2, Minimize2, Copy, Check, Download, History, Trash2, Plus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../../supabaseClient';
import './ChatWidget.css';

// Feature flag: il widget esiste solo se REACT_APP_CHAT_ENABLED === 'true'.
// Con flag off l'app si comporta esattamente come prima (window.open verso il GPTs).
export const CHAT_ENABLED = process.env.REACT_APP_CHAT_ENABLED === 'true';

const GPTS_URL = 'https://chatgpt.com/g/g-68c9d06b6eec81919a2e7d61ed7919c4-sustain';

// Rendering custom delle tabelle markdown: card scrollabile, colonne numeriche
// allineate a destra, badge colorati per i livelli di confidenza.
const textOf = (children) =>
  React.Children.toArray(children)
    .map((c) => {
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      return c?.props?.children ? textOf(c.props.children) : '';
    })
    .join('');

const NUMERIC_CELL_RE = /^[\s0-9.,%~<>≈±+()/–\-−]*\d[\s0-9.,%~<>≈±+()/–\-−]*$/;
const BADGE_CLASS = {
  high: 'chat-badge-high',
  alta: 'chat-badge-high',
  medium: 'chat-badge-medium',
  media: 'chat-badge-medium',
  low: 'chat-badge-low',
  bassa: 'chat-badge-low',
};

const markdownComponents = {
  table: ({ node, ...rest }) => (
    <div className="chat-table-wrap">
      <table {...rest} />
    </div>
  ),
  td: ({ node, children, ...rest }) => {
    const text = textOf(children).trim();
    const badgeClass = BADGE_CLASS[text.toLowerCase()];
    if (badgeClass) {
      return (
        <td {...rest}>
          <span className={`chat-badge ${badgeClass}`}>{text}</span>
        </td>
      );
    }
    return (
      <td {...rest} className={NUMERIC_CELL_RE.test(text) ? 'num' : undefined}>
        {children}
      </td>
    );
  },
};

const WELCOME = {
  role: 'assistant',
  content:
    "Hi! I'm the sustAId LCA assistant. I can answer questions about the environmental " +
    'impacts of textile fibers (CO2, water, energy…) citing the sources of our open dataset. ' +
    'Try asking: *"What is the CO2 impact of cotton?"* — you can write in any language.',
};

// Stessa finestra di storia accettata dalla Edge Function (MAX_HISTORY_MESSAGES):
// riprendendo conversazioni lunghe si invia solo la coda.
const HISTORY_WINDOW = 20;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolActivity, setToolActivity] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Thread dello storico lato server (migration 010): la Edge Function lo crea
  // alla prima domanda e lo comunica con l'evento SSE 'meta'; noi lo rimandiamo
  // alle richieste successive.
  const conversationIdRef = useRef(null);
  // Vista storico: lista delle conversazioni dell'utente (RLS: vede solo le sue)
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [conversations, setConversations] = useState(null); // null = da caricare
  const [historyError, setHistoryError] = useState(null);

  const openHistory = useCallback(async () => {
    setView('history');
    setHistoryError(null);
    const { data, error: err } = await supabase
      .from('chat_conversations')
      .select('id, title, updated_at')
      .order('updated_at', { ascending: false })
      .limit(50);
    if (err) {
      setHistoryError('Could not load your history. Please try again.');
      setConversations([]);
      return;
    }
    setConversations(data ?? []);
  }, []);

  const resumeConversation = useCallback(async (conv) => {
    setHistoryError(null);
    const { data, error: err } = await supabase
      .from('chat_messages')
      .select('role, content')
      .eq('conversation_id', conv.id)
      .order('created_at', { ascending: true });
    if (err || !data) {
      setHistoryError('Could not load the conversation. Please try again.');
      return;
    }
    conversationIdRef.current = conv.id;
    setMessages([WELCOME, ...data.map((m) => ({ role: m.role, content: m.content }))]);
    setError(null);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const deleteConversation = useCallback(async (conv) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the conversation "${conv.title}"?`)) return;
    const { error: err } = await supabase
      .from('chat_conversations')
      .delete()
      .eq('id', conv.id);
    if (err) {
      setHistoryError('Delete failed. Please try again.');
      return;
    }
    setConversations((prev) => (prev ?? []).filter((c) => c.id !== conv.id));
    if (conversationIdRef.current === conv.id) {
      // il thread attivo non esiste più: la prossima domanda ne apre uno nuovo
      conversationIdRef.current = null;
    }
  }, []);

  const newConversation = useCallback(() => {
    conversationIdRef.current = null;
    setMessages([WELCOME]);
    setError(null);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Apertura dall'esterno (pulsanti in Navigation / MaterialsDatabase)
  useEffect(() => {
    const onOpen = (e) => {
      setIsOpen(true);
      const prefill = e?.detail?.prefill;
      if (prefill) setInput(prefill);
      setTimeout(() => inputRef.current?.focus(), 100);
    };
    window.addEventListener('sustaid:open-chat', onOpen);
    return () => window.removeEventListener('sustaid:open-chat', onOpen);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, toolActivity]);

  const sendMessage = useCallback(async () => {
    const question = input.trim();
    if (!question || isStreaming) return;

    setError(null);
    setInput('');
    const outgoing = [...messages, { role: 'user', content: question }];
    setMessages([...outgoing, { role: 'assistant', content: '' }]);
    setIsStreaming(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Session expired: please log in again.');

      // La history inviata esclude il messaggio di benvenuto (solo UI) ed è
      // limitata alla finestra che la Edge Function accetta comunque.
      const history = outgoing.filter((m) => m !== WELCOME).slice(-HISTORY_WINDOW);

      const resp = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            messages: history,
            conversation_id: conversationIdRef.current,
          }),
        }
      );

      if (!resp.ok) {
        let msg = `Service error (${resp.status}).`;
        try {
          const j = await resp.json();
          if (j?.error) msg = j.error;
        } catch (_) { /* body non JSON */ }
        throw new Error(msg);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop(); // l'ultimo pezzo può essere incompleto

        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(6));
          } catch (_) {
            continue;
          }

          if (payload.type === 'text') {
            setToolActivity(false);
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: last.content + payload.text };
              return next;
            });
          } else if (payload.type === 'meta') {
            if (payload.conversation_id) {
              conversationIdRef.current = payload.conversation_id;
            }
          } else if (payload.type === 'tool') {
            setToolActivity(true);
          } else if (payload.type === 'error') {
            throw new Error(payload.message);
          }
        }
      }
    } catch (e) {
      setError(e.message || 'Unexpected error.');
      // rimuove l'eventuale bolla assistant rimasta vuota
      setMessages((prev) =>
        prev[prev.length - 1]?.role === 'assistant' && prev[prev.length - 1].content === ''
          ? prev.slice(0, -1)
          : prev
      );
    } finally {
      setIsStreaming(false);
      setToolActivity(false);
    }
  }, [input, isStreaming, messages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = async (index, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch (_) {
      setError('Copy failed: enable clipboard permissions in your browser.');
    }
  };

  const exportConversation = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const md = [
      `# sustAId AI Assistant — conversation of ${stamp}`,
      '',
      ...messages.map((m) =>
        m.role === 'user' ? `**You:**\n\n${m.content}` : `**sustAId AI:**\n\n${m.content}`
      ),
    ].join('\n\n---\n\n');
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sustaid-chat-${stamp}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!CHAT_ENABLED) return null;

  return (
    <>
      {/* Pulsante flottante */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 z-40 flex items-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-green-700 transition-colors"
          aria-label="Open AI assistant"
        >
          <MessageSquare className="w-5 h-5" />
          <span className="hidden sm:inline text-sm font-semibold">AI Assistant</span>
        </button>
      )}

      {/* Pannello chat */}
      {isOpen && (
        <div
          className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-40 w-full bg-white sm:rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{
            maxWidth: isExpanded ? 'min(90vw, 800px)' : 448,
            height: isExpanded ? '85vh' : 'min(600px, 85vh)',
            transition: 'max-width 0.25s ease, height 0.25s ease',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-green-600 text-white px-4 py-3">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5" />
              <div>
                <div className="text-sm font-bold">sustAId AI Assistant</div>
                <div className="text-xs text-green-100">
                  LCA data with sources · sustAId Open Dataset
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => (view === 'history' ? setView('chat') : openHistory())}
                disabled={isStreaming}
                title={view === 'history' ? 'Back to chat' : 'Conversation history'}
                className={`p-1.5 rounded transition-colors ${
                  isStreaming ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                } ${view === 'history' ? 'bg-green-700' : ''}`}
                aria-label="Conversation history"
              >
                <History className="w-4 h-4" />
              </button>
              <button
                onClick={exportConversation}
                title="Download the conversation (.md)"
                className="p-1.5 rounded hover:bg-green-700 transition-colors"
                aria-label="Download the conversation"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded((v) => !v)}
                title={isExpanded ? 'Shrink' : 'Expand'}
                className="hidden sm:block p-1.5 rounded hover:bg-green-700 transition-colors"
                aria-label={isExpanded ? 'Shrink the panel' : 'Expand the panel'}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <a
                href={GPTS_URL}
                target="_blank"
                rel="noreferrer"
                title="Open the external GPT (previous version)"
                className="p-1.5 rounded hover:bg-green-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-green-700 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {view === 'history' ? (
            /* Storico conversazioni (salvate lato server, RLS per utente) */
            <div className="flex-1 overflow-y-auto bg-gray-50 flex flex-col">
              <div className="flex-1 px-4 py-3 space-y-2">
                <button
                  onClick={newConversation}
                  className="w-full flex items-center justify-center space-x-2 border border-green-600 text-green-700 rounded-xl px-3 py-2 text-sm font-semibold hover:bg-green-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>New conversation</span>
                </button>

                {historyError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
                    {historyError}
                  </div>
                )}
                {conversations === null && !historyError && (
                  <p className="text-xs text-gray-500 text-center py-4">Loading…</p>
                )}
                {conversations?.length === 0 && !historyError && (
                  <p className="text-xs text-gray-500 text-center py-4">
                    No saved conversations yet: they are saved automatically as you write.
                  </p>
                )}
                {(conversations ?? []).map((c) => (
                  <div
                    key={c.id}
                    className={`flex items-center bg-white border rounded-xl px-3 py-2 shadow-sm ${
                      conversationIdRef.current === c.id ? 'border-green-500' : 'border-gray-200'
                    }`}
                  >
                    <button
                      onClick={() => resumeConversation(c)}
                      className="flex-1 text-left min-w-0"
                      title="Resume this conversation"
                    >
                      <div className="text-sm text-gray-800 font-medium truncate">{c.title}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(c.updated_at).toLocaleDateString()}{' '}
                        {new Date(c.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </button>
                    <button
                      onClick={() => deleteConversation(c)}
                      className="p-1.5 ml-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete the conversation"
                      aria-label={`Delete the conversation ${c.title}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 bg-white px-3 py-2 text-center text-gray-400" style={{ fontSize: 10 }}>
                Conversations are saved to your account and visible only to you · delete them anytime
              </div>
            </div>
          ) : (
          <>
          {/* Messaggi */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`rounded-2xl px-4 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-green-600 text-white rounded-br-md'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                  }`}
                  style={{ maxWidth: m.role === 'user' ? '85%' : '96%' }}
                >
                  {m.role === 'assistant' ? (
                    <div className="chat-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    m.content
                  )}
                </div>
                {m.role === 'assistant' && m.content && !(isStreaming && i === messages.length - 1) && (
                  <button
                    onClick={() => copyMessage(i, m.content)}
                    className="flex items-center space-x-1 mt-1 ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{ fontSize: 11 }}
                    title="Copy the reply text"
                  >
                    {copiedIndex === i ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            ))}

            {toolActivity && (
              <div className="flex items-center space-x-2 text-xs text-gray-500 pl-1">
                <Database className="w-3.5 h-3.5 animate-pulse" />
                <span>Consulting the LCA database…</span>
              </div>
            )}
            {isStreaming && !toolActivity && messages[messages.length - 1]?.content === '' && (
              <div className="flex items-center space-x-1 pl-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 bg-white px-3 py-3">
            <div className="flex items-end space-x-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder="Ask about LCA data…"
                className="flex-1 resize-none border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={isStreaming}
              />
              <button
                onClick={sendMessage}
                disabled={isStreaming || !input.trim()}
                className={`bg-green-600 text-white p-3 rounded-xl transition-colors ${
                  isStreaming || !input.trim()
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-700'
                }`}
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-gray-400 mt-2 text-center" style={{ fontSize: 10 }}>
              Answers based on the sustAId Open LCA Dataset · always check the cited sources
            </div>
          </div>
          </>
          )}
        </div>
      )}
    </>
  );
};

// Helper per aprire il widget da altri componenti (senza prop drilling)
export const openChatWidget = (prefill) => {
  window.dispatchEvent(new CustomEvent('sustaid:open-chat', { detail: { prefill } }));
};

export default ChatWidget;
